import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { dateForOffset, useApp } from '../state/app.js';
import { Avatar, Spinner } from '../ui/common.js';
import { LeaveStatusChip } from '../ui/status.js';
import { mmdd, nameFromUpn } from '../lib/format.js';
import { RejectModal } from '../modals/RejectModal.js';

function appliedLabel(iso: string): string {
  const ymd = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date(iso));
  return `${mmdd(ymd)} 상신`;
}

export function Approve(): JSX.Element {
  const { me, flash } = useApp();
  const qc = useQueryClient();
  const canApprove = me.permissions.canApproveLeave;
  const [reject, setReject] = useState<{ id: string; name: string } | null>(null);

  const pending = useQuery({ queryKey: ['leave', 'pending'], queryFn: () => api.leavePending() });
  const today = dateForOffset(0).date;
  const board = useQuery({ queryKey: ['board', today], queryFn: () => api.board(today) });

  const dir = useMemo(() => {
    const m = new Map<string, { name: string; dept: string }>();
    for (const e of board.data?.entries ?? []) m.set(e.upn, { name: e.name, dept: e.dept });
    return m;
  }, [board.data]);

  const items = pending.data?.items ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['leave'] });

  const approve = useMutation({
    mutationFn: (id: string) => api.approveLeave(id),
    onSuccess: (_d, _id) => { flash('휴가를 승인했습니다'); refresh(); },
    onError: (e) => flash(e instanceof Error ? e.message : '승인 실패'),
  });

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', animation: 'dxfade .3s ease' }}>
      {!canApprove && (
        <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '11px 15px', marginBottom: 14, fontSize: 13, color: '#9A3412', display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{ background: '#FB923C', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5 }}>참조</span>
          휴가 승인 권한은 대표이사(CEO)에게 있습니다. 현재는 읽기 전용으로 열람만 가능합니다.
        </div>
      )}
      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px 13px', display: 'flex', alignItems: 'center', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>승인 대기 휴가</div>
          <span style={{ marginLeft: 9, background: '#FEE2E2', color: '#B91C1C', fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 999 }}>{items.length}건</span>
        </div>
        {pending.isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>승인 대기 중인 휴가가 없습니다.</div>
        ) : items.map((l, i) => {
          const info = dir.get(l.requesterUpn);
          const name = info?.name ?? nameFromUpn(l.requesterUpn);
          const dept = info?.dept ?? '';
          const range = `${mmdd(l.startDate)}${l.endDate !== l.startDate ? ` ~ ${mmdd(l.endDate)}` : ''}`;
          return (
            <div key={l.id} style={{ padding: '15px 18px', borderBottom: '1px solid #F6F7F9', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <Avatar name={name} index={i} size={30} />
              <div style={{ minWidth: 120 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{name} <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 400 }}>{dept}</span></div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>{appliedLabel(l.requestedAt)}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, background: '#EFF4FE', color: '#1D4ED8', padding: '3px 10px', borderRadius: 6 }}>{l.type}</span>
                <span style={{ fontSize: 13, color: '#6B7280' }}>{range} · {l.days}일</span>
              </div>
              <div style={{ flex: 1, minWidth: 120, fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.reason ?? ''}</div>
              {canApprove ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={() => setReject({ id: l.id, name })} disabled={approve.isPending}>반려</button>
                  <button className="dx-btn dx-btn-primary dx-btn-sm" onClick={() => approve.mutate(l.id)} disabled={approve.isPending}>승인</button>
                </div>
              ) : (
                <LeaveStatusChip s={l.status} />
              )}
            </div>
          );
        })}
      </div>

      {reject && (
        <RejectModal
          name={reject.name}
          onClose={() => setReject(null)}
          onConfirm={async (comment) => {
            await api.rejectLeave(reject.id, comment);
            flash('휴가를 반려했습니다');
            refresh();
          }}
        />
      )}
    </div>
  );
}
